import React, { useState, useEffect } from 'react';
import { 
  db, 
  collection, 
  onSnapshot, 
  doc, 
  updateDoc, 
  deleteDoc,
  query,
  where,
  getDocs,
  writeBatch,
  getDoc
} from '../../firebase';
import {
  Calendar,
  MapPin,
  ChevronRight,
  Plus,
  ArrowLeft,
  ChevronDown,
  User,
  Layout,
  BookOpen,
  Edit3,
  Trash2,
  CalendarDays,
  Clock,
  Search,
  Archive
} from 'lucide-react';
import CreateSpaceModal from './CreateSpaceModal';
import EditSpaceModal from './EditSpaceModal';
import CreateReservationModal from './CreateReservationModal';
import SpaceReservations from './SpaceReservations';
import MyReservations from './MyReservations';
import WeekCalendar from './WeekCalendar';
import styles from '../CSS/Reservas.module.css';

// ✅ ROLES HARDCODEADOS DEL SISTEMA (igual que en App.jsx)
const SYSTEM_ROLES = {
  admin: {
    id: 'admin',
    name: 'Administrador',
    description: 'Acceso completo a todas las funcionalidades del sistema',
    permissions: ['gestion', 'correos', 'clases', 'reservas', 'incidencias', 'admin'],
    emailPermissions: {
      allowedOUs: [],
      canManageAll: true
    },
    classPermissions: {
      allowedGroups: [],
      canManageAll: true
    },
    reservationPermissions: {
      advancedPermissions: ['createSpaces', 'editSpaces', 'deleteSpaces', 'createPeriodicReservations', 'delegateReservations']
    }
  },
  user: {
    id: 'user',
    name: 'Usuario',
    description: 'Sin permisos por defecto - usar grupos para asignar permisos',
    permissions: [],
    emailPermissions: {
      allowedOUs: [],
      canManageAll: false
    },
    classPermissions: {
      allowedGroups: [],
      canManageAll: false
    },
    reservationPermissions: {
      advancedPermissions: []
    }
  }
};

const Reservas = ({ user, granularPermissions }) => {
  const [spaces, setSpaces] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [selectedSpace, setSelectedSpace] = useState(null);
  const [showCreateSpaceModal, setShowCreateSpaceModal] = useState(false);
  const [showEditSpaceModal, setShowEditSpaceModal] = useState(false);
  const [showCreateReservationModal, setShowCreateReservationModal] = useState(false);
  const [showSpaceReservations, setShowSpaceReservations] = useState(false);
  const [showMyReservations, setShowMyReservations] = useState(false);
  const [loading, setLoading] = useState(true);
  const [imagesLoading, setImagesLoading] = useState(true);
  const [viewMode, setViewMode] = useState('spaces'); // 'spaces' o 'cabinets'
  const [nameFilter, setNameFilter] = useState('');
  const [sortOrder, setSortOrder] = useState('name-asc'); // 'name-asc', 'name-desc', 'date-newest', 'date-oldest', 'reservations-most', 'reservations-least'
  const [error, setError] = useState(null);

  const [userPermissions, setUserPermissions] = useState({
    isAdmin: false,
    canCreateSpaces: false,
    canEditSpaces: false,
    canDeleteSpaces: false,
    canCreatePeriodicReservations: false,
    canDelegateReservations: false
  });

  // ⭐ MEJORADO: CARGAR PERMISOS GRANULADOS (rol + grupos)
  useEffect(() => {
    const loadUserPermissions = async () => {
      if (!user?.role) {
        setUserPermissions({
          isAdmin: false,
          canCreateSpaces: false,
          canEditSpaces: false,
          canDeleteSpaces: false,
          canCreatePeriodicReservations: false,
          canDelegateReservations: false
        });
        return;
      }

      try {
        // ✅ PRIORIDAD: Usar permisos granulados si están disponibles
        if (granularPermissions && granularPermissions.reservationPermissions) {
          console.log('✅ [Reservas] Usando permisos granulados:', granularPermissions);

          const hasAdminPermission = granularPermissions.basePermissions?.includes('admin');
          const advancedPermissions = granularPermissions.reservationPermissions.advancedPermissions || [];

          const permissions = {
            isAdmin: hasAdminPermission,
            canCreateSpaces: hasAdminPermission || advancedPermissions.includes('createSpaces'),
            canEditSpaces: hasAdminPermission || advancedPermissions.includes('editSpaces'),
            canDeleteSpaces: hasAdminPermission || advancedPermissions.includes('deleteSpaces'),
            canCreatePeriodicReservations: hasAdminPermission || advancedPermissions.includes('createPeriodicReservations'),
            canDelegateReservations: hasAdminPermission || advancedPermissions.includes('delegateReservations')
          };

          console.log('🔐 [Reservas] Permisos granulados calculados:', permissions);
          setUserPermissions(permissions);
          return;
        }

        // ⚠️ FALLBACK: Usar sistema antiguo de roles
        console.log('⚠️ [Reservas] Usando sistema de roles (fallback)');

        let roleData = null;

        // ✅ Primero verificar si es un rol del sistema
        if (SYSTEM_ROLES[user.role]) {
          console.log('✅ [Reservas] Usando rol del sistema:', user.role);
          roleData = SYSTEM_ROLES[user.role];
        } else {
          // ✅ Si no, buscar en Firestore (roles personalizados)
          console.log('🔍 [Reservas] Buscando rol personalizado en Firestore:', user.role);
          const roleDoc = await getDoc(doc(db, 'roles', user.role));

          if (roleDoc.exists()) {
            console.log('✅ [Reservas] Rol personalizado encontrado:', user.role);
            roleData = roleDoc.data();
          } else {
            console.warn('⚠️ [Reservas] Rol no encontrado:', user.role);
            return;
          }
        }

        // ✅ Calcular permisos del rol
        if (roleData) {
          const hasReservasPermission = roleData.permissions?.includes('reservas');
          const hasAdminPermission = roleData.permissions?.includes('admin');
          const advancedPermissions = roleData.reservationPermissions?.advancedPermissions || [];

          const permissions = {
            isAdmin: hasAdminPermission,
            canCreateSpaces: hasAdminPermission || advancedPermissions.includes('createSpaces'),
            canEditSpaces: hasAdminPermission || advancedPermissions.includes('editSpaces'),
            canDeleteSpaces: hasAdminPermission || advancedPermissions.includes('deleteSpaces'),
            canCreatePeriodicReservations: hasAdminPermission || advancedPermissions.includes('createPeriodicReservations'),
            canDelegateReservations: hasAdminPermission || advancedPermissions.includes('delegateReservations')
          };

          console.log('🔐 [Reservas] Permisos del rol calculados:', permissions);
          setUserPermissions(permissions);
        }
      } catch (error) {
        console.error('❌ [Reservas] Error cargando permisos del usuario:', error);
      }
    };

    loadUserPermissions();
  }, [user?.role, granularPermissions]);

  useEffect(() => {
    setLoading(true);

    const spacesRef = collection(db, 'spaces');
    const unsubscribeSpaces = onSnapshot(
      spacesRef,
      (snapshot) => {
        const spacesData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data()
        }));
        setSpaces(spacesData);
        setLoading(false);
      },
      (error) => {
        console.error('Error al obtener espacios:', error);
        setError('Error al obtener datos');
        setLoading(false);
      }
    );

    const reservationsRef = collection(db, 'reservations');
    const unsubscribeReservations = onSnapshot(
      reservationsRef,
      (snapshot) => {
        const reservationsData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data()
        }));
        setReservations(reservationsData);
      },
      (error) => {
        console.error('Error al obtener reservas:', error);
        setError('Error al obtener datos');
      }
    );

    return () => {
      unsubscribeSpaces();
      unsubscribeReservations();
    };
  }, []);

  // ✅ Efecto para precargar todas las imágenes
  useEffect(() => {
    // Si no hay espacios aún, mantener el loading
    if (spaces.length === 0) {
      setImagesLoading(true);
      return;
    }

    // Obtener todas las URLs de imágenes de los espacios
    const imageUrls = spaces
      .filter(space => space.imageUrl)
      .map(space => space.imageUrl);

    // Si no hay imágenes, no hay nada que cargar
    if (imageUrls.length === 0) {
      setImagesLoading(false);
      return;
    }

    setImagesLoading(true);

    // Precargar todas las imágenes
    const imagePromises = imageUrls.map(url => {
      return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve(url);
        img.onerror = () => resolve(url); // Resolver incluso si falla para no bloquear
        img.src = url;
      });
    });

    // Esperar a que todas las imágenes se carguen
    Promise.all(imagePromises)
      .then(() => {
        console.log('✅ Todas las imágenes cargadas');
        setImagesLoading(false);
      })
      .catch(error => {
        console.error('❌ Error cargando imágenes:', error);
        setImagesLoading(false); // Mostrar de todas formas
      });
  }, [spaces]);

  const formatYMD = (date) => {
    return date.toISOString().split('T')[0];
  };
  
  // ✅ FUNCIÓN: Calcula las reservas activas (hoy o en el futuro)
  const getActiveReservationsCount = (spaceName) => {
    const todayStr = formatYMD(new Date());
    return reservations.filter(res => 
      res.space === spaceName && 
      res.date >= todayStr
    ).length;
  };
  
  // ✅ FUNCIÓN: Calcula el total de reservas para un espacio
  const getTotalReservationsCount = (spaceName) => {
    return reservations.filter(res => res.space === spaceName).length;
  };


  const handleOpenCreateSpace = () => setShowCreateSpaceModal(true);
  const handleCloseCreateSpace = () => setShowCreateSpaceModal(false);

  const handleOpenEditSpace = () => setShowEditSpaceModal(true);
  const handleCloseEditSpace = () => setShowEditSpaceModal(false);

  const handleOpenCreateReservation = () => setShowCreateReservationModal(true);
  const handleCloseCreateReservation = () => setShowCreateReservationModal(false);

  const toggleSpaceReservations = () => {
    setShowSpaceReservations(!showSpaceReservations);
    if (!showSpaceReservations) {
      setShowMyReservations(false);
    }
  };

  const toggleMyReservations = () => {
    setShowMyReservations(!showMyReservations);
    if (!showMyReservations) {
      setShowSpaceReservations(false);
    }
  };

  const handleDeselectSpace = () => {
    setSelectedSpace(null);
    setShowSpaceReservations(false);
    setShowMyReservations(false);
  };

  const handleDeleteSpace = async () => {
    if (!selectedSpace) return;

    const confirmDelete = window.confirm(
      `¿Estás seguro de que deseas eliminar el espacio "${selectedSpace.name}"?\n\n` +
      `Esta acción también eliminará todas las reservas asociadas a este espacio.`
    );

    if (confirmDelete) {
      try {
        const reservationsQuery = query(
          collection(db, 'reservations'),
          where('space', '==', selectedSpace.name)
        );
        const reservationsSnapshot = await getDocs(reservationsQuery);

        const batch = writeBatch(db);
        reservationsSnapshot.forEach((docSnapshot) => {
          batch.delete(doc(db, 'reservations', docSnapshot.id));
        });

        batch.delete(doc(db, 'spaces', selectedSpace.id));

        await batch.commit();

        console.log('✅ Espacio y reservas eliminados exitosamente');
        setSelectedSpace(null);
      } catch (error) {
        console.error('❌ Error al eliminar el espacio:', error);
        alert('Hubo un error al eliminar el espacio. Por favor, inténtalo de nuevo.');
      }
    }
  };

  const handleSpaceUpdate = (updatedSpace) => {
    setSelectedSpace(updatedSpace);
  };

  // Filtrar y ordenar espacios
  const filteredSpaces = spaces
    .filter(space => {
      // Si no tiene tipo, se considera 'spaces' por defecto (compatibilidad con espacios anteriores)
      const spaceType = space.type || 'spaces';
      const matchesType = spaceType === viewMode;
      const matchesName = space.name.toLowerCase().includes(nameFilter.toLowerCase());
      return matchesType && matchesName;
    })
    .sort((a, b) => {
      switch (sortOrder) {
        case 'name-asc':
          return a.name.localeCompare(b.name);
        case 'name-desc':
          return b.name.localeCompare(a.name);
        case 'date-newest':
          return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
        case 'date-oldest':
          return new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
        case 'reservations-most':
          return getActiveReservationsCount(b.name) - getActiveReservationsCount(a.name);
        case 'reservations-least':
          return getActiveReservationsCount(a.name) - getActiveReservationsCount(b.name);
        default:
          return 0;
      }
    });

  if (loading || imagesLoading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingSpinner}></div>
        <p className={styles.loadingText}>Cargando...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.errorContainer}>
        <p className={styles.errorMessage}>{error}</p>
      </div>
    );
  }

  return (
    <div className={styles.reservasContainer}>
      <main className={styles.mainContent}>
        <div className={styles.contentWrapper}>
          {!selectedSpace ? (
            <div className={styles.spacesSection}>
              <div className={styles.sectionHeader}>
                <h2 className={styles.sectionTitle}>
                  {viewMode === 'spaces' ? (
                    <>
                      <Layout className={styles.sectionIcon} />
                      Espacios Disponibles
                    </>
                  ) : (
                    <>
                      <Archive className={styles.sectionIcon} />
                      Armarios Disponibles
                    </>
                  )}
                </h2>
                <div className={styles.headerControls}>
                  {/* Switch para cambiar entre espacios y armarios */}
                  <div className={styles.viewModeSwitch}>
                    <button
                      className={`${styles.switchButton} ${viewMode === 'spaces' ? styles.switchButtonActive : ''}`}
                      onClick={() => setViewMode('spaces')}
                    >
                      <Layout className={styles.switchIcon} />
                      <span>Espacios</span>
                    </button>
                    <button
                      className={`${styles.switchButton} ${viewMode === 'cabinets' ? styles.switchButtonActive : ''}`}
                      onClick={() => setViewMode('cabinets')}
                    >
                      <Archive className={styles.switchIcon} />
                      <span>Armarios</span>
                    </button>
                  </div>

                  {/* Filtro de nombres */}
                  <div className={styles.filterGroup}>
                    <div className={styles.searchContainer}>
                      <Search className={styles.searchIcon} />
                      <input
                        type="text"
                        placeholder="Buscar por nombre..."
                        value={nameFilter}
                        onChange={(e) => setNameFilter(e.target.value)}
                        className={styles.searchInput}
                      />
                    </div>

                    <div className={styles.sortContainer}>
                      <select
                        value={sortOrder}
                        onChange={(e) => setSortOrder(e.target.value)}
                        className={styles.sortSelect}
                      >
                        <option value="name-asc">
                          Nombre A-Z
                        </option>
                        <option value="name-desc">
                          Nombre Z-A
                        </option>
                        <option value="date-newest">
                          Más recientes
                        </option>
                        <option value="date-oldest">
                          Más antiguos
                        </option>
                        <option value="reservations-most">
                          Más reservas
                        </option>
                        <option value="reservations-least">
                          Menos reservas
                        </option>
                      </select>
                    </div>
                  </div>

                  {/* Botón crear */}
                  {userPermissions.canCreateSpaces && (
                    <button
                      className={styles.btnCreateSpace}
                      onClick={handleOpenCreateSpace}
                    >
                      <Plus className={styles.buttonIcon} />
                      <span>Crear</span>
                    </button>
                  )}
                </div>
              </div>

                <div className={styles.spacesGrid}>
                  {filteredSpaces.map((space) => {
                    // ✅ CORRECCIÓN: Calcular el conteo de reservas para cada espacio
                    const reservationCount = getActiveReservationsCount(space.name);
                    
                    return (
                      <div
                        key={space.id}
                        className={styles.spaceCard}
                        data-has-image={space.imageUrl ? "true" : "false"}
                        style={
                          space.imageUrl
                            ? {
                                backgroundImage: `url(${space.imageUrl})`,
                              }
                            : {}
                        }
                        onClick={() => setSelectedSpace(space)}
                      >
                        {/* 🆕 Badge de ubicación - solo se muestra si existe */}
                        {space.location && (
                          <div className={styles.locationBadge}>
                            <MapPin className={styles.locationIcon} />
                            {space.location}
                          </div>
                        )}

                        <div className={styles.spaceCardContent}>
                          <h3 className={styles.spaceName}>{space.name}</h3>
                          <div className={styles.spaceStats}>
                            <div className={styles.statItem}>
                              <Calendar className={styles.statIcon} />
                              <span>{reservationCount} reservas</span>
                            </div>
                          </div>
                        </div>
                        
                        
                      </div>
                    );
                  })}
                </div>
            </div>
          ) : (
            <div className={styles.selectedSpacePanel}>
              <div className={styles.spaceHeaderCompact}>
              
                <div className={styles.spaceHeaderLeft}>
                  <button
                    className={styles.backButton}
                    onClick={handleDeselectSpace}
                    title="Volver a la lista de espacios"
                  >
                    <ArrowLeft className={styles.backIcon} />
                  </button>
                  <h2 className={styles.selectedSpaceTitle}>{selectedSpace.name}</h2>
                </div>

                <div className={styles.spaceActions}>
                  {/* Grupo: Acciones principales */}
                  <div className={styles.actionGroup}>
                    <button
                      className={styles.btnPrimary}
                      onClick={handleOpenCreateReservation}
                    >
                      <Plus className={styles.buttonIcon} />
                      Crear Reserva
                    </button>
                  </div>

                  {/* Grupo: Consultar reservas */}
                  <div className={styles.actionGroup}>
                    <button
                      className={styles.btnSecondary}
                      onClick={toggleSpaceReservations}
                    >
                      <Calendar className={styles.buttonIcon} />
                      {showSpaceReservations ? 'Ocultar' : 'Ver'} Reservas
                    </button>
                    <button
                      className={styles.btnSecondary}
                      onClick={toggleMyReservations}
                    >
                      <BookOpen className={styles.buttonIcon} />
                      Mis Reservas
                    </button>
                  </div>

                  {/* Grupo: Gestión del espacio (solo si tiene permisos) */}
                  {(userPermissions.canEditSpaces || userPermissions.canDeleteSpaces) && (
                    <div className={styles.actionGroup}>
                      {userPermissions.canEditSpaces && (
                        <button
                          className={styles.btnSecondary}
                          onClick={handleOpenEditSpace}
                        >
                          <Edit3 className={styles.buttonIcon} />
                          Editar
                        </button>
                      )}
                      {userPermissions.canDeleteSpaces && (
                        <button
                          className={styles.btnSecondary}
                          onClick={handleDeleteSpace}
                        >
                          <Trash2 className={styles.buttonIcon} />
                          Eliminar
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {showSpaceReservations && (
                <div className={styles.reservationsContainer}>
                  <SpaceReservations
                    space={selectedSpace}
                    onClose={() => setShowSpaceReservations(false)}
                  />
                </div>
              )}

              {showMyReservations && (
                <div className={styles.reservationsContainer}>
                  <MyReservations
                    user={user}
                    space={selectedSpace}
                    onClose={() => setShowMyReservations(false)}
                    canDelegateReservations={userPermissions.canDelegateReservations}
                  />
                </div>
              )}

              <div className={styles.calendarContainer}>
                <WeekCalendar 
                  user={user} 
                  space={selectedSpace}
                  canCreatePeriodicReservations={userPermissions.canCreatePeriodicReservations}
                  canDelegateReservations={userPermissions.canDelegateReservations}
                />
              </div>
            </div>
          )}
        </div>
      </main>

      {showCreateSpaceModal && (
        <CreateSpaceModal
          onClose={handleCloseCreateSpace}
          spaces={spaces}
          type={viewMode}
        />
      )}

      {showEditSpaceModal && selectedSpace && (
        <EditSpaceModal
          onClose={handleCloseEditSpace}
          space={selectedSpace}
          spaces={spaces}
          onUpdate={handleSpaceUpdate}
        />
      )}

      {showCreateReservationModal && selectedSpace && (
        <CreateReservationModal
          user={user}
          space={selectedSpace}
          onClose={handleCloseCreateReservation}
          canCreatePeriodicReservations={userPermissions.canCreatePeriodicReservations}
          canDelegateReservations={userPermissions.canDelegateReservations}
        />
      )}
    </div>
  );
};

export default Reservas;